import React from "react";
import { Card } from "../ui/card";
import { Activity, Brain, Camera, BarChart3, Bell, Droplets, Smartphone } from "lucide-react";

// Feature data with associated screenshot info
const features = [
  {
    icon: Camera,
    title: "Computer Vision Analysis",
    description: "Advanced AI analyzes your skin using the Melanin Index, Erythema Index, and Individual Typology Angle to detect early signs of sun damage.",
    gradient: "from-orange-500 to-coral-500",
    screenshot: "/screenshots/skin-analysis.jpg", // Update this path when you add your screenshot
    screenshotAlt: "AI-powered skin analysis interface"
  },
  {
    icon: Activity,
    title: "Real-Time UV Tracking",
    description: "Integrates live weather data and UV index information to provide accurate, location-based sun exposure monitoring.",
    gradient: "from-coral-500 to-pink-500",
    screenshot: "/screenshots/home-screen.jpg", // Home screen with UV tracking
    screenshotAlt: "Real-time UV tracking dashboard"
  },
  {
    icon: Brain,
    title: "Personalized Recommendations",
    description: "Receive custom SPF suggestions and safe tanning times based on your skin type, location, and current UV levels.",
    gradient: "from-amber-500 to-orange-500",
    screenshot: "/screenshots/uv-index.jpg", // UV Index screen with SPF recommendations
    screenshotAlt: "Personalized UV recommendations"
  },
  {
    icon: BarChart3,
    title: "Progress Visualization",
    description: "Track your skin health journey with beautiful data visualizations showing changes over time and exposure patterns.",
    gradient: "from-pink-500 to-coral-500",
    screenshot: "/screenshots/home-screen.jpg", // Home screen with graphs
    screenshotAlt: "Progress tracking visualizations"
  },
  {
    icon: Bell,
    title: "Smart Alerts",
    description: "Get instant notifications when excessive exposure is detected or when skin redness levels indicate it's time to seek shade.",
    gradient: "from-orange-500 to-amber-500",
    screenshot: "/screenshots/weather-screen.jpg", // Weather screen with alerts
    screenshotAlt: "Smart sun safety alerts"
  },
  {
    icon: Droplets,
    title: "Skin Health Insights",
    description: "Understand your skin's response to sun exposure with scientific measurements and actionable health recommendations.",
    gradient: "from-coral-500 to-orange-500",
    screenshot: "/screenshots/uv-index.jpg", // UV Index with health metrics
    screenshotAlt: "Skin health insights dashboard"
  }
];

export default function FeaturesWithScreenshots() {
  const [theme, setTheme] = React.useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  React.useEffect(() => {
    const handleStorage = () => {
      setTheme(localStorage.getItem('theme') || 'dark');
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return (
    <div className={`py-20 md:py-32 ${
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
            theme === 'dark'
              ? 'bg-orange-500/10 border border-orange-500/20'
              : 'bg-orange-50'
          }`}>
            <Activity className={`w-4 h-4 ${
              theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
            }`} />
            <span className={`text-sm font-medium ${
              theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
            }`}>Powerful Features</span>
          </div>
          <h2 className={`text-4xl md:text-5xl font-bold mb-6 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Technology Meets
            <span className="bg-gradient-to-r from-orange-600 to-coral-600 bg-clip-text text-transparent"> Sun Safety</span>
          </h2>
          <p className={`text-xl max-w-3xl mx-auto ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Combining engineering, health technology, and AI to help you make safer choices for your skin
          </p>
        </div>

        {/* Features Grid with Screenshots */}
        <div className="space-y-20">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-8 lg:gap-12 items-center`}
            >
              {/* Screenshot Section */}
              <div className="flex-1 w-full">
                <div className={`relative rounded-3xl overflow-hidden shadow-2xl ${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
                }`}>
                  {/* Phone Frame */}
                  <div className="relative mx-auto" style={{ maxWidth: '300px', paddingTop: '200%' }}>
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                      {/* Screenshot Image */}
                      <img 
                        src={feature.screenshot} 
                        alt={feature.screenshotAlt}
                        className="w-full h-full object-contain rounded-2xl"
                        onError={(e) => {
                          // Fallback if image doesn't exist yet
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Placeholder if image doesn't exist */}
                      <div 
                        className={`hidden w-full h-full items-center justify-center rounded-2xl ${
                          theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
                        }`}
                        style={{ minHeight: '400px' }}
                      >
                        <div className="text-center p-8">
                          <Smartphone className={`w-16 h-16 mx-auto mb-4 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                          }`} />
                          <p className={`text-sm ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            Screenshot: {feature.title}
                          </p>
                          <p className={`text-xs mt-2 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-500'
                          }`}>
                            Add image to: public/screenshots/
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Phone Frame Border Effect */}
                    <div className={`absolute inset-0 rounded-3xl border-4 ${
                      theme === 'dark' ? 'border-gray-700' : 'border-gray-300'
                    }`} />
                  </div>
                  {/* Glow Effect */}
                  <div className={`absolute -inset-1 bg-gradient-to-r ${feature.gradient} opacity-20 blur-xl -z-10`} />
                </div>
              </div>

              {/* Feature Content Section */}
              <div className="flex-1 w-full">
                <Card 
                  className={`p-8 md:p-10 border-2 h-full ${
                    theme === 'dark'
                      ? 'bg-gray-900 border-gray-700 hover:border-orange-500'
                      : 'border-gray-100 hover:border-orange-200'
                  } hover:shadow-xl transition-all duration-300`}
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-lg`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {feature.title}
                  </h3>
                  <p className={`text-lg leading-relaxed ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {feature.description}
                  </p>
                </Card>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="mt-20 text-center">
          <div className={`inline-block rounded-2xl p-8 md:p-12 ${
            theme === 'dark'
              ? 'bg-gradient-to-br from-orange-600/10 to-coral-600/10 border border-orange-500/20'
              : 'bg-gradient-to-br from-orange-50 to-coral-50 border border-orange-200'
          }`}>
            <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Experience Sun Safety Technology
            </h3>
            <p className={`text-lg mb-6 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Download the app and start protecting your skin today
            </p>
            <a 
              href="https://apps.apple.com/us/app/sunscreened/id6756286578" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <button className={`px-8 py-4 rounded-xl font-semibold text-lg transition-all ${
                theme === 'dark'
                  ? 'bg-gradient-to-r from-orange-500 to-coral-500 text-white hover:from-orange-600 hover:to-coral-600 shadow-lg hover:shadow-xl'
                  : 'bg-gradient-to-r from-orange-500 to-coral-500 text-white hover:from-orange-600 hover:to-coral-600 shadow-lg hover:shadow-xl'
              }`}>
                Download Now
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}



